import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Checkbox, darken, Divider, Fab, Link, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import IconButton from "@mui/material/IconButton";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useQueryClient } from '@tanstack/react-query';
import _ from 'lodash';
import React, { lazy, Suspense, useEffect, useState } from "react";
import AttachmentInput from '../attachment/attachment.input';
import AttachmentList from '../attachment/attachment.list';
import { useDialogs, useMenu } from '../common/dialog.util';
import InlineEditableField from '../common/inline.editable.field';
import { courseDetailApi } from './courseDetail.queries';

const CourseDetailDialog = lazy(() => import('./courseDetail.dialog'));

function CourseDetailRow(props) {
	const queryClient = useQueryClient();
	const useSave = courseDetailApi.useSave();
	const useDelete = courseDetailApi.useDelete();
	const useAddBefore = courseDetailApi.useAddBefore();
	const useAddAfter = courseDetailApi.useAddAfter();

	let { course, courseDetail, groups, depth } = props;
	const menu = useMenu();
	const dialogs = useDialogs();
	const theme = useTheme();

	const [expand, setExpand] = useState(props.expand);
	const [hoverPosition, setHoverPosition] = useState(null);

	useEffect(() => {
		if (props.expandAll !== null) {
			setExpand(props.expandAll);
		}
	}, [props.expandAll]);

	if (!courseDetail?.id) {
		return;
	}

	const toggleExpand = () => setExpand(!expand);

	const openEdit = () => {
		menu.close();
		dialogs.open('courseDetail', courseDetail);
	};

	const addBefore = async () => {
		await useAddBefore.mutateAsync(courseDetail.id);
	}

	const addAfter = async () => {
		await useAddAfter.mutateAsync(courseDetail.id);
	}

	const openAdd = () => {
		const reference = { ...courseDetail };
		const newCourseDetail = { reference, courseId: courseDetail.courseId, parentId: courseDetail.parentId, type: courseDetail.type, name: courseDetail.type, ordinal: courseDetail.ordinal };
		menu.close();
		dialogs.open('courseDetail', newCourseDetail);
	};

	const onSelect = async (courseDetail, file) => {
		courseDetail.attachments = courseDetail.attachments ? courseDetail.attachments : [];
		courseDetail.attachments.push(file);
	};

	const onUpload = async (courseDetail, file) => {
		_.remove(courseDetail.attachments, { fileName: file.fileName });
		courseDetail.attachments.push(file);
		queryClient.invalidateQueries(['course', courseDetail.courseId]);
	};

	const onDeleteAttachment = async (courseDetail, attachment) => {
		_.remove(courseDetail.attachments, { id: attachment.id });
		queryClient.invalidateQueries(['course', courseDetail.courseId]);
	};

	const onDelete = async () => {
		await useDelete.mutateAsync(courseDetail);
		menu.close();
	};

	const hover = (e) => {
		const tr = e.target.closest('tr');
		if (!tr) {
			setHoverPosition(null);
			return;
		}
		const rect = tr.getBoundingClientRect();
		const midpoint = rect.top + rect.height / 2;

		const newHoverPosition = e.clientY < midpoint ? 'top' : 'bottom';
		if (newHoverPosition != hoverPosition) {
			setHoverPosition(newHoverPosition);
		}
	};

	const hoverLeave = (e) => {
		setHoverPosition(null);
	};

	const trSx = {
		position: 'relative',
		'th,td': {
			verticalAlign: 'top',
			'&:not(.MuiTableCell-paddingCheckbox)': {
				pt: 0.5,
			},
			backgroundColor: darken(theme.palette.background.paper, depth / 40),
		},
		'&:hover': {
			'> *': {
				backgroundColor: theme.palette.action.hover,
			},
			'.MuiOutlinedInput-notchedOutline': {
				borderWidth: 1,
			},
		},
		'&:focus-within': {
			'> *': {
				backgroundColor: theme.palette.action.selected,
			},
		},
		'.MuiOutlinedInput-notchedOutline': {
			borderWidth: 0,
		},
	};

	const size = 14;
	const fabSx = {
		position: 'absolute',
		left: 1 + (-size / 2),
		minWidth: size,
		minHeight: size,
		width: size,
		height: size,
		svg: {
			height: size,
			width: size,
		}
	};

	return (
		<React.Fragment>
			<TableRow className="hover-toggle" sx={[trSx, { '> *': { borderBottomWidth: expand ? 1 : 0 } }]} onMouseEnter={hover} onMouseLeave={hoverLeave} onMouseMove={hover}>
				<TableCell className="hover-toggle" sx={{ pl: depth * 2 }}>
					{hoverPosition == 'top' &&
						<Fab className="hover-subtle" color="primary" sx={[fabSx, { top: (-size / 2) }]} onClick={addBefore}>
							<AddIcon />
						</Fab>
					}
					{props.children}
					{hoverPosition == 'bottom' &&
						<Fab className="hover-subtle" color="primary" sx={[fabSx, { bottom: (-size / 2) }]} onClick={addAfter}>
							<AddIcon />
						</Fab>
					}
				</TableCell>
				<TableCell sx={{ pl: depth * 2 }}>
					{groups?.[courseDetail.id]?.length > 0 &&
						<IconButton onClick={toggleExpand}>
							{expand ? <KeyboardArrowUpIcon fontSize="inherit" /> : <KeyboardArrowDownIcon fontSize="inherit" />}
						</IconButton>
					}
				</TableCell>
				<TableCell scope="row" sx={{ pl: depth * 2 }}>
					<InlineEditableField model={courseDetail} field="name" mutator={useSave} />
				</TableCell>
				<TableCell>
					<InlineEditableField model={courseDetail} field="description" mutator={useSave} slots={{ input: { multiline: true } }} />
				</TableCell>
				<TableCell>
					<AttachmentList attachments={courseDetail.attachments} onDelete={(attachment) => onDeleteAttachment(courseDetail, attachment)} />
					<Link component="label" role={undefined} className="hover hover-toggle pointer">
						<Stack spacing={1} className="hover-muted" sx={{ mt: courseDetail.attachments?.length ? 0 : 2 }}>
							<AttachFileIcon sx={{ fontSize: 16 }} />
							<Typography sx={{ fontSize: 11 }}>Add</Typography>
						</Stack>
						<AttachmentInput attachableType="courseDetail" attachableId={courseDetail.id} onSelect={(file) => onSelect(courseDetail, file)} onUpload={(file) => onUpload(courseDetail, file)} />
					</Link>
				</TableCell>
				<TableCell>
					<IconButton className="hover-muted" onClick={(e) => {menu.open(e); setExpand(true)}}>
						<MoreVertIcon fontSize="inherit" />
					</IconButton>
					{menu.isOpen &&
						<Menu open={menu.isOpen} onClose={menu.close} anchorEl={menu.anchor} anchorOrigin={{ horizontal: 'left', vertical: 'top' }} transformOrigin={{ horizontal: 'right', vertical: 'top' }} >
							<MenuItem onClick={openAdd}>Add More</MenuItem>
							<Divider />
							<MenuItem onClick={onDelete}>Delete</MenuItem>
						</Menu>
					}
					{dialogs.isOpen?.courseDetail && (
						<Suspense fallback={null}>
							<CourseDetailDialog courseDetail={dialogs.data} open={dialogs.isOpen?.courseDetail == true} onSubmit={dialogs.close} onCancel={dialogs.close} />
						</Suspense>
					)}
				</TableCell>
			</TableRow>
			{expand && groups?.[courseDetail.id]?.length > 0 && _.chain(groups[courseDetail.id]).sortBy('ordinal').map(cd =>
				<CourseDetailRow course={course} courseDetail={cd} groups={groups} depth={depth + 1} key={cd.id} expandAll={props.expandAll} />
			).value()}
		</React.Fragment>
	);
}

export default React.memo(CourseDetailRow);